import { FC } from 'react'
import { Alert, Flex, Select, Table } from 'antd'
import { usePerformanceParams } from 'core/queryParams/usePerformanceParams'
import PrLink from 'pages/Dashboard/PrLink'
import { ColumnsType } from 'antd/lib/table'
import { useGetBuildMetrics } from 'lib/queries/useGetBuildMetrics/useGetBuildMetrics'
import { BuildMetricsRecord } from 'lib/queries/useGetBuildMetrics/buildMetrics.types'
import { METRIC_APPS } from './apps'
import ColumnHelpTitle from './ColumnHelpTitle/ColumnHelpTitle'
import MetricDeltaCell from './MetricDeltaCell/MetricDeltaCell'
import {
  RELATIVE_REGRESSION_THRESHOLD,
  formatSignedBytes,
  formatSignedCoverage,
  formatBytes,
  formatCompileTime,
  formatCoverage,
  isAtLeastPercentWorse,
} from './performanceMetric.utils'
import styles from './PerformanceMetrics.less'

const HASH_SIZE_LENGTH = 7

const isCurrentMain = (record: BuildMetricsRecord): boolean => {
  return record.filename.endsWith('/main.json')
}

const changeFromMain = (
  current: number | null,
  format: (value: number | null) => string,
  baseline?: number | null
): string => {
  if (current === null || baseline === null || baseline === undefined) {
    return '—'
  }

  return format(current - baseline)
}

const formatGzipChangeFromMain = (record: BuildMetricsRecord): string => {
  const compared = record.comparedToMain
  const gzipBytes = record.bundle.gzipBytes
  const mainGzipBytes = compared?.gzipBytes ?? null
  const cannotCompareGzipSize =
    compared === null || gzipBytes === null || mainGzipBytes === null

  if (cannotCompareGzipSize) {
    return '—'
  }

  const sizeChangeFromMain = formatSignedBytes(gzipBytes - mainGzipBytes)

  if (compared.exactBase) {
    return sizeChangeFromMain
  }

  return `${sizeChangeFromMain} vs main at ${compared.sha.slice(0, HASH_SIZE_LENGTH)}`
}

const columnDefinitions: ColumnsType<BuildMetricsRecord> = [
  {
    title: 'Built at',
    dataIndex: 'builtAt',
    key: 'builtAt',
    render: (builtAt: string) => new Date(builtAt).toLocaleString(),
    sorter: (a, b) =>
      new Date(a.builtAt).getTime() - new Date(b.builtAt).getTime(),
  },
  {
    title: 'Branch',
    dataIndex: 'branchName',
    key: 'branchName',
    render: (branchName: string, record) =>
      isCurrentMain(record) ? 'Current main' : branchName,
  },
  {
    title: 'Pull request',
    dataIndex: 'pullRequestNumber',
    key: 'pullRequestNumber',
    render: (pullRequestNumber: string | null) =>
      pullRequestNumber ? <PrLink prNumber={pullRequestNumber} /> : '—',
  },
  {
    title: 'Gzipped size',
    key: 'gzipBytes',
    render: (_, record) => formatBytes(record.bundle.gzipBytes),
  },
  {
    title: (
      <ColumnHelpTitle
        label="Gzip size vs main"
        helpText="Compared with current main when this build's merge base still matches. If main has moved, the change is against the last main snapshot we have for that merge base, not the pull request's latest commit."
        testId="gzipVsMainHelp"
      />
    ),
    key: 'gzipChangeFromMain',
    render: (_, record) => (
      <MetricDeltaCell
        value={formatGzipChangeFromMain(record)}
        showFlag={isAtLeastPercentWorse({
          current: record.bundle.gzipBytes,
          baseline: record.comparedToMain?.gzipBytes,
          percent: RELATIVE_REGRESSION_THRESHOLD,
          direction: 'increase',
        })}
        flagHelpText="Gzipped size is 10% or more larger than main."
        flagTestId="gzipVsMainRegression"
      />
    ),
  },
  {
    title: 'Compile time',
    key: 'compileTimeMs',
    render: (_, record) => formatCompileTime(record.compileTimeMs),
  },
  {
    title: 'Statement coverage',
    key: 'statementsPercentage',
    render: (_, record) => formatCoverage(record.coverage.statementsPercentage),
  },
  {
    title: 'Coverage vs main',
    key: 'coverageChangeFromMain',
    render: (_, record) => (
      <MetricDeltaCell
        value={changeFromMain(
          record.coverage.statementsPercentage,
          formatSignedCoverage,
          record.comparedToMain?.statementsPercentage
        )}
        showFlag={isAtLeastPercentWorse({
          current: record.coverage.statementsPercentage,
          baseline: record.comparedToMain?.statementsPercentage,
          percent: RELATIVE_REGRESSION_THRESHOLD,
          direction: 'decrease',
        })}
        flagHelpText="Statement coverage is 10% or more lower than main."
        flagTestId="coverageVsMainRegression"
      />
    ),
  },
]

const columns: ColumnsType<BuildMetricsRecord> = columnDefinitions.map(
  (column) => ({
    ...column,
    onCell: (record) => ({
      className: isCurrentMain(record) ? styles.currentMainCell : undefined,
    }),
  })
)

const PerformanceMetrics: FC = () => {
  const {
    query: { app },
    setQuery,
  } = usePerformanceParams()
  const { data, isLoading, isError } = useGetBuildMetrics(app)
  const records = data ?? []
  const rows = [
    ...records.filter(isCurrentMain),
    ...records.filter((record) => !isCurrentMain(record)),
  ]

  return (
    <Flex vertical gap="middle">
      <Select
        data-test-id="buildMetricsAppSelect"
        value={app}
        options={METRIC_APPS}
        onChange={(nextApp) => setQuery({ app: nextApp })}
      />
      {isError ? (
        <Alert
          type="error"
          message="Couldn't load build metrics."
          data-test-id="buildMetricsError"
        />
      ) : (
        <Table
          data-test-id="buildMetricsTable"
          columns={columns}
          dataSource={rows}
          loading={isLoading}
          rowKey="filename"
          pagination={{ position: ['bottomCenter'], defaultPageSize: 30 }}
        />
      )}
    </Flex>
  )
}

export default PerformanceMetrics
